/**
 * The Work (Docit Work) half of the KnowIt adapter: tiles, search, sprints, views, settings.
 *
 * Like the automation routes these answer bespoke envelopes — `{ pagination, data, facets,
 * policies }` for lists, `{ data, policies }` for one tile — so the translation lives beside
 * `knowitAutomation.ts` and is handed the adapter's transport: bearer injected, 401 refreshed. A
 * raw `fetch` from a scene arrives anonymous (the World-page outage of 2026-08-18).
 *
 * Wire shapes are the server's DTOs (09 §2); the scenes type them in `@docit/work`'s
 * `workTypes.ts` and validate lightly. Errors keep the server's `{ ok:false, error, message }`
 * envelope so a 409 `stale` can re-render truth and a 400 `query-invalid` can name the term.
 */

export interface WorkTransport {
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
}

export interface WorkListOptions {
  projectId?: string | null;
  query?: string;
  offset?: number;
  limit?: number;
  facets?: boolean;
}

export interface WorkCreateInput {
  projectId: string;
  kind: string;
  title: string;
  description?: string | null;
  parentId?: string | null;
  priority?: string | null;
  severity?: string | null;
  estimate?: number | null;
  dueAt?: string | null;
  area?: string | null;
  labels?: string[];
  assignee?: { kind: string; id?: string | null } | null;
  tetherUserId?: string | null;
  sprintId?: string | null;
}

export interface WorkPatch {
  title?: string;
  description?: string;
  priority?: string;
  /** Empty string clears. */
  severity?: string;
  estimate?: number | null;
  clearEstimate?: boolean;
  dueAt?: string | null;
  clearDueAt?: boolean;
  /** Empty string clears. */
  area?: string;
  labels?: string[];
  /** The all-zero GUID clears. */
  sprintId?: string;
  blocked?: boolean;
  blockedReason?: string;
}

/** A plan is addressed by its project (key or id) and the `planRef` it was applied with. */
export interface WorkPlanRef {
  /** Read/act in this workspace (membership-checked) instead of the session's. */
  teamId?: string;
  projectKey?: string;
  projectId?: string;
  planRef: string;
}

export interface WorkPlanApproveInput extends WorkPlanRef {
  /** Omitted ⇒ the whole plan. */
  keys?: string[];
  /**
   * The flow version the reviewer SAW, per item key. A flow that changed since is refused as
   * `plan_changed` instead of being approved unseen.
   */
  versions?: Record<string, number>;
  note?: string;
}

/**
 * `work.plan.apply` — file a plan (the MCP `work_plan` wire). Items are the planner's own shape
 * (ref, kind, title, description, acceptance, dependsOn, steps, children); re-applying the same
 * `planRef` never duplicates, and a changed item flow goes back to PROPOSED.
 */
export interface WorkPlanApplyInput {
  /** Act in this workspace (membership-checked, audited) — the repo's own, not the browser's. */
  teamId?: string;
  projectKey?: string;
  projectId?: string;
  planRef: string;
  source?: { title?: string; text?: string; documentId?: string; conversationId?: string };
  parentKey?: string;
  flows?: "auto" | "per-item" | "none";
  launch?: "none";
  dryRun?: boolean;
  repoPath?: string;
  items: unknown[];
}

/** `work.plan.retry` — answer the plan's open help questions `retry` (addressee only). Omitted keys ⇒ every item. */
export interface WorkPlanRetryInput extends WorkPlanRef {
  keys?: string[];
}

/**
 * Cancellation + lineage (2026-09-27). `work.lineage` answers the tree plan → items → flows → runs →
 * nodes → tile/dispatch/questions around ONE anchor; exactly one address is given.
 */
export type WorkLineageInput =
  | { planRef: string; projectKey: string }
  | { runId: string }
  | { key: string }
  | { dispatchId: string };

/**
 * `work.cancel` — always previewed first (`dryRun: true`), then applied with the same body. `target`
 * per scope: `nodeId@runId` (step) | runId (run) | KEY (item) | planRef (plan, with `projectKey`).
 */
export interface WorkCancelInput {
  scope: "step" | "run" | "item" | "plan";
  target: string;
  planRef?: string;
  projectKey?: string;
  reason: string;
  /** plan: keep | cancel · item: obsolete | wont_fix | duplicate. */
  mode?: string;
  /** item only: its children too. */
  cascade?: boolean;
  dryRun: boolean;
  /** Another workspace of the same person (Docit Work 23 §cross-workspace); the server acts as the caller's row there. */
  teamId?: string;
}

/** `work.cancel.reconcile` — finish what an earlier cancel or failure left open (plan or runs). */
export interface WorkReconcileInput {
  planRef?: string;
  projectKey?: string;
  runIds?: string[];
  reason?: string;
  dryRun: boolean;
  /** Another workspace of the same person (Docit Work 23 §cross-workspace). */
  teamId?: string;
}

export interface WorkApi {
  /**
   * Weave — the workspace as things that can be put on ONE stage, each with its measured thinness.
   * Choosing what to aggregate decides whether anything is testable, so the picker shows the number
   * that decides it: how many days carry any activity.
   */
  aggregateInventory(): Promise<unknown>;
  /** Weave — run the causality engine over the stage the reader assembled. */
  aggregateCausality(input: {
    projectIds?: string[];
    collectionIds?: string[];
    windowDays?: number;
  }): Promise<unknown>;
  /** The wire vocabulary: kinds, states + categories, resolutions, priorities, severities, link types, parent rules. */
  vocabulary(): Promise<unknown>;
  projects(): Promise<unknown>;
  /** Docit Work 30 — the Chief: how the work is going, as a verdict per group and a few plain questions. */
  chief(input?: { projectId?: string; projectKey?: string; planRef?: string }): Promise<unknown>;
  items: {
    list(options?: WorkListOptions): Promise<unknown>;
    search(
      query: string,
      options?: { offset?: number; limit?: number; facets?: boolean },
    ): Promise<unknown>;
    info(ref: { id?: string; key?: string }, include?: string[]): Promise<unknown>;
    /**
     * The tile's LIVE SESSION (docit-work 26): the agent steps working it — who runs each, since when,
     * the prompt it was given — and the selected one's lines (prompt, thinking, tool calls, output).
     * No cursor ⇒ the newest page; `after` ⇒ the lines since (tailing); `before` ⇒ the page above.
     */
    session(
      ref: { id?: string; key?: string },
      options?: { dispatchId?: string; after?: number; before?: number },
    ): Promise<unknown>;
    tree(options: {
      rootId?: string;
      projectId?: string;
      depth?: number;
      /** Weave: also return each tile's rollup and the flow node it is bound to. */
      rollup?: boolean;
      run?: boolean;
    }): Promise<unknown>;
    /**
     * Weave (15 §4.3) — a person reports on a tile: evidence, links, and the state they leave it
     * in. When a run is parked waiting for this tile, the report is what resumes it.
     */
    /**
     * Weave (15 §4.1) — hold (or release) the flow node a tile is bound to, optionally with its
     * whole subtree. A held node does not start; the run says who stopped it.
     */
    /**
     * Weave — the work as a CAUSAL graph, read the way the news theatre reads causality. Two classes
     * of edge, never merged: `declared` (a person said so) and `measured` (the corpus supports it,
     * evidenced by the documents and files the tiles share). `health.verdict` says why there is
     * nothing to draw when there is nothing to draw.
     */
    causality(input: { projectId: string; windowDays?: number }): Promise<unknown>;
    hold(ref: { id?: string; key?: string }, hold: boolean, subtree?: boolean): Promise<unknown>;
    /**
     * Human involvement (2026-10-02) — how much of this item's flow the person wants to see:
     * every-step | milestones | warnings | problems (default) | none, where it comes from
     * (item / plan / project / default) and the policy it produces. Set works mid-run.
     */
    involvement(ref: { id?: string; key?: string }): Promise<unknown>;
    setInvolvement(ref: { id?: string; key?: string }, level: string | null): Promise<unknown>;
    release(
      ref: { id?: string; key?: string },
      input: {
        state: string;
        summary?: string;
        evidence?: Array<{ kind: string; ref?: string; url?: string }>;
        ifMatch?: number;
      },
    ): Promise<unknown>;
    create(input: WorkCreateInput): Promise<unknown>;
    update(
      ref: { id?: string; key?: string },
      ifMatch: number | undefined,
      patch: WorkPatch,
    ): Promise<unknown>;
    transition(
      ref: { id?: string; key?: string },
      to: string,
      options?: { resolution?: string; comment?: string; ifMatch?: number },
    ): Promise<unknown>;
    assign(
      ref: { id?: string; key?: string },
      assignee: { kind: string; id?: string | null } | null,
      ifMatch?: number,
    ): Promise<unknown>;
    retether(ref: { id?: string; key?: string }, userId: string | null): Promise<unknown>;
    watch(ref: { id?: string; key?: string }, watch: boolean): Promise<unknown>;
    reparent(
      ref: { id?: string; key?: string },
      parentId: string | null,
      ifMatchTree?: number,
    ): Promise<unknown>;
    convert(ref: { id?: string; key?: string }, kind: string, ifMatch?: number): Promise<unknown>;
    link(
      ref: { id?: string; key?: string },
      type: string,
      target: { kind: string; id?: string; key?: string; url?: string },
    ): Promise<unknown>;
    unlink(
      ref: { id?: string; key?: string },
      type: string,
      target: { kind: string; id?: string; key?: string; url?: string },
    ): Promise<unknown>;
    comment(ref: { id?: string; key?: string }, body: string, tag?: string): Promise<unknown>;
    comments(
      ref: { id?: string; key?: string },
      options?: { offset?: number; limit?: number },
    ): Promise<unknown>;
    history(
      ref: { id?: string; key?: string },
      options?: { offset?: number; limit?: number },
    ): Promise<unknown>;
    merge(
      ref: { id?: string; key?: string },
      into: { id?: string; key?: string },
    ): Promise<unknown>;
    delete(ref: { id?: string; key?: string }): Promise<unknown>;
    restore(ref: { id?: string; key?: string }): Promise<unknown>;
  };
  sprints: {
    list(options?: { projectId?: string; status?: string }): Promise<unknown>;
    create(input: {
      projectId: string;
      name: string;
      goal?: string;
      startsAt?: string;
      endsAt?: string;
    }): Promise<unknown>;
    start(id: string): Promise<unknown>;
    close(id: string, carryOverToSprintId?: string): Promise<unknown>;
  };
  views: {
    list(projectId?: string): Promise<unknown>;
    create(input: {
      name: string;
      query: string;
      view?: string;
      visibility?: string;
      projectId?: string;
      pinned?: boolean;
    }): Promise<unknown>;
    delete(id: string): Promise<unknown>;
  };
  settings: {
    project(projectId: string): Promise<unknown>;
    setProject(input: Record<string, unknown> & { projectId: string }): Promise<unknown>;
  };
  /**
   * List → work (19): the owner's review of a whole plan before anything runs — every item, every
   * generated flow, every node's executor and full prompt — and the one approval that lets it run.
   */
  plan: {
    /** File (or re-file) a plan; nothing launches — every flow it makes is PROPOSED. */
    apply(input: WorkPlanApplyInput): Promise<unknown>;
    get(input: WorkPlanRef): Promise<unknown>;
    approve(input: WorkPlanApproveInput): Promise<unknown>;
    /** Retry every failed / declined / expired agent step of the plan (its open help questions). */
    retry(input: WorkPlanRetryInput): Promise<unknown>;
  };
  /** The lineage tree around one anchor (plan, run, item key or dispatch). Read-only. */
  /** Bench (work-bench 04 §1): this person's boards in this workspace. */
  bench: {
    get(): Promise<unknown>;
    set(patch: Record<string, unknown>): Promise<unknown>;
  };
  /** Bench (work-bench 04 §2): phased plans — the ai-map phase table as tiles with model + effort per phase. */
  phased: {
    apply(input: Record<string, unknown>): Promise<unknown>;
    get(input: { key?: string; planRef?: string; projectKey?: string }): Promise<unknown>;
    list(projectKey?: string | null): Promise<unknown>;
  };
  phase: {
    handover(key: string): Promise<unknown>;
    report(input: {
      key: string;
      result: string;
      files?: string[];
      evidence?: string;
      note?: string;
    }): Promise<unknown>;
    skip(key: string, reason?: string): Promise<unknown>;
  };
  /** Bench (work-bench 03 §1): send a tile to one of the person's IDE windows — an arrival, or a run on a named model. */
  send(input: Record<string, unknown>): Promise<unknown>;
  /** Bench (work-bench 06): what deciding a tile reaches — branches and leaves, graded; the engine's co-activity marked. */
  influence(key: string, options?: { depth?: number; measure?: boolean }): Promise<unknown>;
  lineage(input: WorkLineageInput): Promise<unknown>;
  /** Cancel a step, run, item or plan — `{ dryRun, effects, counts, already }`. */
  cancel(input: WorkCancelInput): Promise<unknown>;
  /** Reconcile leftovers of a plan or of runs — same effect shape as `cancel`. */
  reconcile(input: WorkReconcileInput): Promise<unknown>;
  /** Escape hatch for endpoints without a typed method yet (every `work.*` verb answers here). */
  call(method: string, body?: Record<string, unknown>): Promise<unknown>;
}

export function createKnowItWork(transport: WorkTransport): WorkApi {
  const post = (method: string, body: Record<string, unknown> = {}) => transport.post(method, body);
  return {
    aggregateInventory: () => post("knowledge.aggregate.inventory"),
    aggregateCausality: (input) => post("knowledge.aggregate.causality", { ...input }),
    vocabulary: () => post("work.vocabulary"),
    projects: () => post("work.projects.list"),
    chief: (input = {}) => post("work.chief", { ...input }),
    items: {
      list: (options = {}) => post("work.items.list", { ...options }),
      search: (query, options = {}) => post("work.items.search", { query, ...options }),
      info: (ref, include) => post("work.items.info", { ...ref, include }),
      session: (ref, options = {}) => post("work.items.session", { ...ref, ...options }),
      tree: (options) => post("work.items.tree", { ...options }),
      causality: (input) => post("work.items.causality", { ...input }),
      hold: (ref, hold, subtree) => post("work.items.hold", { ...ref, hold, subtree }),
      involvement: (ref) => post("work.involvement", { ...ref }),
      setInvolvement: (ref, level) => post("work.involvement.set", { ...ref, level }),
      release: (ref, input) => post("work.items.release", { ...ref, ...input }),
      create: (input) => post("work.items.create", { ...input }),
      update: (ref, ifMatch, patch) => post("work.items.update", { ...ref, ifMatch, patch }),
      transition: (ref, to, options = {}) =>
        post("work.items.transition", { ...ref, to, ...options }),
      assign: (ref, assignee, ifMatch) => post("work.items.assign", { ...ref, assignee, ifMatch }),
      retether: (ref, userId) => post("work.items.retether", { ...ref, userId }),
      watch: (ref, watch) => post(watch ? "work.items.watch" : "work.items.unwatch", { ...ref }),
      reparent: (ref, parentId, ifMatchTree) =>
        post("work.items.reparent", { ...ref, parentId, ifMatchTree }),
      convert: (ref, kind, ifMatch) => post("work.items.convert", { ...ref, kind, ifMatch }),
      link: (ref, type, target) => post("work.items.link", { ...ref, type, target }),
      unlink: (ref, type, target) => post("work.items.unlink", { ...ref, type, target }),
      comment: (ref, body, tag) => post("work.items.comment", { ...ref, body, tag }),
      comments: (ref, options = {}) => post("work.items.comments", { ...ref, ...options }),
      history: (ref, options = {}) => post("work.items.history", { ...ref, ...options }),
      merge: (ref, into) =>
        post("work.items.merge", { ...ref, intoId: into.id, intoKey: into.key }),
      delete: (ref) => post("work.items.delete", { ...ref }),
      restore: (ref) => post("work.items.restore", { ...ref }),
    },
    sprints: {
      list: (options = {}) => post("work.sprints.list", { ...options }),
      create: (input) => post("work.sprints.create", { ...input }),
      start: (id) => post("work.sprints.start", { id }),
      close: (id, carryOverToSprintId) => post("work.sprints.close", { id, carryOverToSprintId }),
    },
    views: {
      list: (projectId) => post("work.views.list", { projectId }),
      create: (input) => post("work.views.create", { ...input }),
      delete: (id) => post("work.views.delete", { id }),
    },
    settings: {
      project: (projectId) => post("work.settings.project.get", { projectId }),
      setProject: (input) => post("work.settings.project.set", { ...input }),
    },
    plan: {
      apply: (input) => post("work.plan.apply", { ...input }),
      get: (input) => post("work.plan.get", { ...input }),
      approve: (input) => post("work.plan.approve", { ...input }),
      retry: (input) => post("work.plan.retry", { ...input }),
    },
    bench: {
      get: () => post("work.bench.get", {}),
      set: (patch) => post("work.bench.set", { ...patch }),
    },
    phased: {
      apply: (input) => post("work.phased.apply", { ...input }),
      get: (input) => post("work.phased.get", { ...input }),
      list: (projectKey) => post("work.phased.list", projectKey ? { projectKey } : {}),
    },
    phase: {
      handover: (key) => post("work.phase.handover", { key }),
      report: (input) => post("work.phase.report", { ...input }),
      skip: (key, reason) => post("work.phase.skip", { key, reason }),
    },
    send: (input) => post("work.items.send", { ...input }),
    influence: (key, options = {}) => post("work.items.influence", { key, ...options }),
    lineage: (input) => post("work.lineage", { ...input }),
    cancel: (input) => post("work.cancel", { ...input }),
    reconcile: (input) => post("work.cancel.reconcile", { ...input }),
    call: (method, body = {}) => post(method, body),
  };
}
